import * as THREE from 'three';
import {
  STAR_RADIUS,
  FIELD_LINE_SEGMENTS_DEFAULT,
  FIELD_LINE_SEGMENT_BUDGET,
  FIELD_LINE_SEGMENTS_MIN,
  FIELD_LINE_COLOR_DEFAULT,
  FIELD_LINE_OPACITY_DEFAULT,
} from './constants';
import type { FieldLineConfig } from './types';

/**
 * Default configuration for field line rendering.
 */
const DEFAULT_CONFIG: FieldLineConfig = {
  fieldLineCount: 6,
  azimuthalPlaneCount: 4,
  fieldLineExtent: 6.0,
  fieldLineColor: FIELD_LINE_COLOR_DEFAULT,
  visible: true,
};

/**
 * FieldLineRenderer generates and renders magnetic dipole field line curves.
 * Field lines are parametric dipole curves distributed across azimuthal planes,
 * oriented along the magnetic axis, and parented to a THREE.Object3D group
 * so they inherit rotation and orbital transforms automatically.
 *
 * Follows the AxisVisualizer pattern: accepts a parent Object3D in constructor,
 * manages its own geometry/material lifecycle.
 */
export class FieldLineRenderer {
  private parent: THREE.Object3D;
  private config: FieldLineConfig;
  private lines: THREE.Line[] = [];
  private material: THREE.LineBasicMaterial;
  private magneticAxis: THREE.Vector3 = new THREE.Vector3(0, 1, 0);
  private disposed = false;
  private segmentBudget: number;
  private maxSegmentsPerCurve: number;

  constructor(parent: THREE.Object3D, config?: Partial<FieldLineConfig>, performanceLimits?: { segmentBudget?: number; maxSegmentsPerCurve?: number }) {
    if (!parent) {
      throw new Error('FieldLineRenderer: parent Object3D is required');
    }

    this.parent = parent;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.segmentBudget = performanceLimits?.segmentBudget ?? FIELD_LINE_SEGMENT_BUDGET;
    this.maxSegmentsPerCurve = performanceLimits?.maxSegmentsPerCurve ?? FIELD_LINE_SEGMENTS_DEFAULT;

    this.material = new THREE.LineBasicMaterial({
      color: this.config.fieldLineColor,
      transparent: true,
      opacity: FIELD_LINE_OPACITY_DEFAULT,
      blending: THREE.AdditiveBlending,
    });

    this.generateFieldLines();
  }

  /**
   * Compute a single vertex on a dipole field line curve.
   * Uses parametric equations:
   *   r = R_max * sin²(θ)
   *   x = r * sin(θ) * cos(φ)
   *   y = r * cos(θ)
   *   z = r * sin(θ) * sin(φ)
   */
  public computeDipoleVertex(theta: number, rMax: number, phi: number): THREE.Vector3 {
    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);
    const r = rMax * sinTheta * sinTheta;

    const x = r * sinTheta * Math.cos(phi);
    const y = r * cosTheta;
    const z = r * sinTheta * Math.sin(phi);

    return new THREE.Vector3(x, y, z);
  }

  /**
   * Compute the number of segments per curve, enforcing the segment budget.
   * Formula: max(16, min(maxSegmentsPerCurve, floor(segmentBudget / (N × M))))
   */
  public computeSegmentsPerCurve(lineCount: number, planeCount: number): number {
    const totalCurves = lineCount * planeCount;
    if (totalCurves <= 0) return this.maxSegmentsPerCurve;

    const computed = Math.floor(this.segmentBudget / totalCurves);
    return Math.max(FIELD_LINE_SEGMENTS_MIN, Math.min(this.maxSegmentsPerCurve, computed));
  }

  /**
   * Generate all field line THREE.Line objects and add to parent.
   * Distributes R_max values linearly from STAR_RADIUS to fieldLineExtent,
   * across M azimuthal planes at uniform angular intervals.
   */
  private generateFieldLines(): void {
    // Remove any existing lines
    this.removeAllLines();

    const { fieldLineCount, azimuthalPlaneCount, fieldLineExtent } = this.config;
    const segmentsPerCurve = this.computeSegmentsPerCurve(fieldLineCount, azimuthalPlaneCount);

    // Compute the quaternion to rotate from reference Y-axis to actual magnetic axis
    const referenceAxis = new THREE.Vector3(0, 1, 0);
    const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
      referenceAxis,
      this.magneticAxis.clone().normalize()
    );

    // Generate field lines across all azimuthal planes
    for (let m = 0; m < azimuthalPlaneCount; m++) {
      const phi = m * (2 * Math.PI / azimuthalPlaneCount);

      for (let n = 0; n < fieldLineCount; n++) {
        // Linearly distribute R_max from STAR_RADIUS to fieldLineExtent
        const rMax = fieldLineCount > 1
          ? STAR_RADIUS + n * (fieldLineExtent - STAR_RADIUS) / (fieldLineCount - 1)
          : STAR_RADIUS;

        // Create pre-allocated buffer geometry
        const maxVertices = segmentsPerCurve + 1;
        const positions = new Float32Array(maxVertices * 3);
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Compute vertices along the dipole curve (θ from 0 to π)
        for (let s = 0; s <= segmentsPerCurve; s++) {
          const theta = (s / segmentsPerCurve) * Math.PI;
          const vertex = this.computeDipoleVertex(theta, rMax, phi);

          // Apply magnetic axis rotation
          vertex.applyQuaternion(rotationQuat);

          positions[s * 3] = vertex.x;
          positions[s * 3 + 1] = vertex.y;
          positions[s * 3 + 2] = vertex.z;
        }

        geometry.setDrawRange(0, segmentsPerCurve + 1);
        (geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;

        const line = new THREE.Line(geometry, this.material);
        line.visible = this.config.visible;
        this.lines.push(line);
        this.parent.add(line);
      }
    }
  }

  /**
   * Remove all line objects from parent and dispose their geometry.
   */
  private removeAllLines(): void {
    for (const line of this.lines) {
      this.parent.remove(line);
      line.geometry.dispose();
    }
    this.lines = [];
  }

  /**
   * Update the magnetic axis orientation.
   * Called when magneticOffsetAngle changes.
   * Only regenerates geometry if the axis actually changed.
   */
  updateMagneticAxis(magneticAxisLocal: THREE.Vector3): void {
    const newAxis = magneticAxisLocal.clone().normalize();
    // Skip regeneration if the axis hasn't changed (within floating-point tolerance)
    if (newAxis.distanceTo(this.magneticAxis) < 1e-6) return;
    this.magneticAxis = newAxis;
    this.regenerateGeometry();
  }

  /**
   * Update configuration parameters.
   * - Geometry-affecting changes (fieldLineCount, azimuthalPlaneCount, fieldLineExtent):
   *   If line count changes, recreate line objects. If only extent changes, reuse existing
   *   BufferGeometry and update vertex positions in place (Req 7.1).
   * - Appearance-only changes (fieldLineColor): update material color without recomputing vertices (Req 7.2).
   * - Visibility changes: delegate to setVisible() (Req 6.2, 6.3).
   */
  updateParams(config: Partial<FieldLineConfig>): void {
    const countChanging = (
      (config.fieldLineCount !== undefined && config.fieldLineCount !== this.config.fieldLineCount) ||
      (config.azimuthalPlaneCount !== undefined && config.azimuthalPlaneCount !== this.config.azimuthalPlaneCount)
    );

    const extentOnlyChanging = (
      config.fieldLineExtent !== undefined &&
      config.fieldLineExtent !== this.config.fieldLineExtent &&
      !countChanging
    );

    // Handle color update (appearance-only, no vertex recomputation)
    if (config.fieldLineColor !== undefined) {
      try {
        this.material.color.set(config.fieldLineColor);
        this.config.fieldLineColor = config.fieldLineColor;
      } catch {
        console.warn(
          `FieldLineRenderer: Invalid color "${config.fieldLineColor}", falling back to default "${FIELD_LINE_COLOR_DEFAULT}".`
        );
        this.material.color.set(FIELD_LINE_COLOR_DEFAULT);
        this.config.fieldLineColor = FIELD_LINE_COLOR_DEFAULT;
      }
    }

    // Handle visibility (delegate to setVisible, no vertex recomputation)
    if (config.visible !== undefined) {
      this.setVisible(config.visible);
    }

    // Handle geometry-affecting changes
    if (countChanging) {
      // Line count changed — must dispose and recreate line objects
      if (config.fieldLineCount !== undefined) this.config.fieldLineCount = config.fieldLineCount;
      if (config.azimuthalPlaneCount !== undefined) this.config.azimuthalPlaneCount = config.azimuthalPlaneCount;
      if (config.fieldLineExtent !== undefined) this.config.fieldLineExtent = config.fieldLineExtent;
      this.regenerateGeometry();
    } else if (extentOnlyChanging) {
      // Only extent changed — reuse existing BufferGeometry, update vertices in place (Req 7.1)
      this.config.fieldLineExtent = config.fieldLineExtent!;
      this.updateVerticesInPlace();
    }
  }

  /**
   * Set visibility of all field line geometry without destroying objects (Req 6.2, 6.3).
   */
  setVisible(visible: boolean): void {
    this.config.visible = visible;
    for (const line of this.lines) {
      line.visible = visible;
    }
  }

  /**
   * Dispose all geometry, materials, and remove from parent.
   * Stub for now — full implementation in task 5.2.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.removeAllLines();
    this.material.dispose();
  }

  /**
   * Regenerate all field line geometry (internal helper).
   * Used when line count changes and objects must be recreated.
   */
  private regenerateGeometry(): void {
    this.generateFieldLines();
  }

  /**
   * Update vertex positions in place on existing BufferGeometry instances.
   * Called when only fieldLineExtent changes but line count stays the same.
   * Reuses existing THREE.Line objects and their BufferGeometry (Req 7.1).
   */
  private updateVerticesInPlace(): void {
    const { fieldLineCount, azimuthalPlaneCount, fieldLineExtent } = this.config;
    const segmentsPerCurve = this.computeSegmentsPerCurve(fieldLineCount, azimuthalPlaneCount);

    // Compute the quaternion to rotate from reference Y-axis to actual magnetic axis
    const referenceAxis = new THREE.Vector3(0, 1, 0);
    const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
      referenceAxis,
      this.magneticAxis.clone().normalize()
    );

    let lineIndex = 0;
    for (let m = 0; m < azimuthalPlaneCount; m++) {
      const phi = m * (2 * Math.PI / azimuthalPlaneCount);

      for (let n = 0; n < fieldLineCount; n++) {
        const rMax = fieldLineCount > 1
          ? STAR_RADIUS + n * (fieldLineExtent - STAR_RADIUS) / (fieldLineCount - 1)
          : STAR_RADIUS;

        const line = this.lines[lineIndex];
        const positionAttribute = line.geometry.getAttribute('position') as THREE.BufferAttribute;
        const positions = positionAttribute.array as Float32Array;

        // Recompute vertices for this line
        for (let s = 0; s <= segmentsPerCurve; s++) {
          const theta = (s / segmentsPerCurve) * Math.PI;
          const vertex = this.computeDipoleVertex(theta, rMax, phi);
          vertex.applyQuaternion(rotationQuat);

          positions[s * 3] = vertex.x;
          positions[s * 3 + 1] = vertex.y;
          positions[s * 3 + 2] = vertex.z;
        }

        positionAttribute.needsUpdate = true;
        line.geometry.setDrawRange(0, segmentsPerCurve + 1);

        lineIndex++;
      }
    }
  }

  /** Expose current config for testing */
  getConfig(): Readonly<FieldLineConfig> {
    return { ...this.config };
  }

  /** Expose lines array for testing */
  getLines(): readonly THREE.Line[] {
    return this.lines;
  }
}
